import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { Outlet } from 'react-router-dom';

import { useStores } from '@src/Common/Hooks/useStores';
import MetaHelmet from '@src/Components/Atoms/Helmet/MetaHelmet';
import { withStore } from '@src/Store/Store.context';

import { Partytown } from '@builder.io/partytown/react';
import {
  Button,
  ModalError,
  ModalUserFeedback,
  ModalUserFeedbackSuccess,
  NavbarAccount
} from '@jupiter/react-common-component';
import useWindowDimensions from '@src/Common/Hooks/useWindowDimensions';
import ButtonBackPortal from '@src/Components/Atoms/ButtonBackPortal/ButtonBackPortal';
import ButtonNavbarItem from '@src/Components/Atoms/ButtonNavbarItem/ButtonNavbarItem';
import ModalAccountRequired from '@src/Components/Molecules/Modal/ModalAccountRequired/ModalAccountRequired';
import ProfileButtonList from '@src/Components/Molecules/ProfileButtonList/ProfileButtonList';
import Sidebar from '@src/Components/Molecules/Sidebar/Sidebar';
import { logEvent, logPageView } from '@src/Domain/Analytic';
import { AnalyticsEvent } from '@src/Domain/Analytic/Analytic.type';
import ReCAPTCHA from 'react-google-recaptcha';
import MaintenancePage from '../MaintenancePage/MaintenancePage';
import useMaintenance from '../MaintenancePage/useMaintenance';
import styles from './Dashboard.module.scss';
import useDashboard from './useDashboard';

const siteKeyCaptchaUserFeedback =
  process.env.REACT_APP_USER_FEEDBACK_CAPTCHA_KEY ?? '';
const showDebugPartyTown = (env: string | undefined) =>
  env === 'release' || env === 'review' ? false : true;
const DashboardLayout = observer(() => {
  const { FormStore, UserStore, UserFeedbackStore, UiStore } = useStores();
  const { isAuthenticated, signinRedirect, isLoading, user, removeUser } =
    useAuth();
  const { width } = useWindowDimensions();
  const {
    handleSignOut,
    handleToPortal,
    portalUrl,
    onSubmitEvent,
    setShowSidebar,
    showSidebar,
    showContent,
    setShowcontent,
    onBtnSubmitEventClick,
    showUserFeedbackForm,
    setShowUserFeedbackForm,
    onUserFeedbackSubmit,
    recaptchaRef,
    closeUserFeedbackForm
  } = useDashboard();
  const { getMaintainStatus, isMaintenance } = useMaintenance();

  useEffect(() => {
    logPageView();

    getMaintainStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const profileImgUrl =
    user?.profile.picture ??
    `${process.env.REACT_APP_GCM_BUCKET_URL}/mythic-portal-v2/img/png/img_default.png`;

  useEffect(() => {
    const oidcStorage = localStorage.getItem(
      `oidc.user:${process.env.BASE_URL_OIDC_AUTHORITY}:${process.env.OIDC_CLIENT_ID}`
    );
    if (oidcStorage && isAuthenticated) {
      const isExpired =
        JSON.parse(oidcStorage).expires_at <
        Math.round(new Date().getTime() / 1000);
      if (isExpired) removeUser();
    }
  }, [isAuthenticated, removeUser]);

  return (
    <>
      <Partytown
        debug={showDebugPartyTown(process.env.REACT_APP_ENV)}
        forward={['dataLayer.push']}
      />
      {isMaintenance ? (
        <MaintenancePage />
      ) : (
        <>
          <MetaHelmet />
          <header className={styles['Header']}>
            <NavbarAccount
              isShowSidebar={showContent}
              handleShowSidebar={() => {
                setShowcontent(!showContent);
              }}
              buttonBack={
                <ButtonBackPortal href={portalUrl} onClick={handleToPortal} />
              }
              isAuthenticated={isAuthenticated}
              loadingBtnLogin={isLoading}
              labelBtnLogin="Login"
              headingTitle={'Decentralized UNIVERSAL Meta'}
              headingSecondTitle="BETA"
              isDum={true}
              targetHref="_self"
              onSignIn={() => {
                signinRedirect();
              }}
              buttonRight={
                <Button
                  label="Submit Event"
                  size="sm"
                  isLoading={false}
                  variant={'default-magenta'}
                  onClick={onSubmitEvent}
                />
              }
              onSignOut={handleSignOut}
              handleFeedback={() => {
                setShowcontent(false);
                setShowUserFeedbackForm((v) => !v);
                logEvent(
                  AnalyticsEvent.BUTTON_CLICKED,
                  'DUM Navbar User Feedback Click'
                );
              }}
              isFeedbackIcon={true}
              profile={{
                nickname: UserStore.adminAccountData.username,
                entitlement: UserStore.adminAccountData.entitlement,
                email: UserStore.adminAccountData.email,
                accountId: '',
                profilePictureUrl: profileImgUrl,
                walletAddress: UserStore.adminAccountData.wallet_address
              }}
              listMenuProfile={
                <section
                  className={styles['bottom-content']}
                  data-testid="profile-button-list"
                >
                  <div className={styles['button-wrapper']}>
                    <ButtonNavbarItem
                      text="Submit Event"
                      type="add"
                      variant="mobile"
                      onClick={() => {
                        onBtnSubmitEventClick();
                        setShowcontent(false);
                      }}
                    />
                  </div>
                  {!!portalUrl && (
                    <div className={styles['button-wrapper']}>
                      <ButtonNavbarItem
                        text="Return to Portal"
                        type="mythic-portal"
                        variant="mobile"
                        href={portalUrl}
                        onClick={handleToPortal}
                      />
                    </div>
                  )}
                </section>
              }
              handleAccountSetting={() => {
                window.location.assign(
                  `${process.env.SSO_DOMAIN}?back_url=${process.env.PORTAL_URL}`
                );
              }}
            />
          </header>
          <div className={styles['content']}>
            <Outlet />
          </div>
          {showUserFeedbackForm && (
            <>
              <ModalUserFeedback
                show={showUserFeedbackForm}
                loading={UserFeedbackStore.status.isLoading}
                onClose={closeUserFeedbackForm}
                onSubmit={onUserFeedbackSubmit}
              />
              <ReCAPTCHA
                className={styles['recaptcha']}
                sitekey={siteKeyCaptchaUserFeedback}
                ref={recaptchaRef}
                size="invisible"
              />
            </>
          )}
          {UserFeedbackStore.status.isSuccess && (
            <ModalUserFeedbackSuccess
              show={UserFeedbackStore.status.isSuccess}
              onClose={closeUserFeedbackForm}
            />
          )}
          {UserFeedbackStore.status.isError && (
            <ModalError
              show={UserFeedbackStore.status.isError}
              message={UserFeedbackStore.status.message}
              title="Error"
              onRetry={() => {
                UserFeedbackStore.status.isError =
                  !UserFeedbackStore.status.isError;
                setShowUserFeedbackForm(true);
              }}
              onClose={() => {
                UserFeedbackStore.status = {
                  isLoading: false,
                  isError: false,
                  isSuccess: false,
                  message: ''
                };
                setShowUserFeedbackForm((s) => !s);
              }}
            />
          )}
          {UiStore.showModalAccountRequired && (
            <ModalAccountRequired
              onClose={() => (UiStore.showModalAccountRequired = false)}
            />
          )}
        </>
      )}
    </>
  );
});

export default withStore(DashboardLayout);
